import { useEffect, useState } from "react";
import { Box, Checkbox, Grid } from "@mui/material";
import styled from "styled-components";
import {
  BoldText,
  CheckboxLabelWrapper,
  CheckboxWrapper,
  NewReqStateGrid,
  NewReqStreetGrid,
  NewRequisitionTextField,
  StyledFormLabel,
  handleKeyDown,
} from "./NewRequisition.styles";
import FormField from "../shared/TextField/FormField";
import { statesArray } from "./types";
import { GetTypographyContent } from "../shared/Typography/Typography";
import {
  FormatedAddress,
  GetGooglePlacesAddress,
  GetGooglePlacesAddressDetails,
} from "./Services/NewReqService";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";

export interface ShipKitShippingInfo {
  firstName: string;
  lastName: string;
  fullAddress: string;
  streetAddress: string;
  aptUnit: string;
  city: string;
  state: string;
  zipCode: string;
}

export const emptyShipKitShippingInfo: ShipKitShippingInfo = {
  firstName: "",
  lastName: "",
  fullAddress: "",
  streetAddress: "",
  aptUnit: "",
  city: "",
  state: "",
  zipCode: "",
};

const ShipKitCheckboxWrapper = styled(CheckboxWrapper)`
  background-color: ${({ theme }) => theme.blueSubtle};
  border: 1px solid ${({ theme }) => theme.blueLight};
`;

const ShipKitTitle = styled(BoldText)`
  color: ${({ theme }) => theme.blueDark};
  font-size: 16px;
`;

interface ShipKitToPatientFormProps {
  mode: "order" | "standalone";
  shipToPatient: boolean;
  onShipToPatientChange: (checked: boolean) => void;
  sameAsPatientAddress: boolean;
  onSameAsPatientToggle: (checked: boolean) => void;
  shippingInfo: ShipKitShippingInfo;
  onShippingInfoChange: (shippingInfo: ShipKitShippingInfo) => void;
  patientAddress: ShipKitShippingInfo;
  disabled?: boolean;
}

const ShipKitToPatientForm: React.FC<ShipKitToPatientFormProps> = ({
  mode,
  shipToPatient,
  onShipToPatientChange,
  sameAsPatientAddress,
  onSameAsPatientToggle,
  shippingInfo,
  onShippingInfoChange,
  patientAddress,
  disabled = false,
}) => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const [autocompleteAddressList, setAutocompleteAddressList] = useState<
    FormatedAddress[]
  >([]);
  const [selectedAutoCompleteAddress, setSelectedAutoCompleteAddress] =
    useState<FormatedAddress>();

  const addressDisabled = sameAsPatientAddress;
  const showForm = mode === "standalone" || shipToPatient;
  const displayInfo = sameAsPatientAddress ? patientAddress : shippingInfo;

  useEffect(() => {
    const getAutocompleteAddresses = async () => {
      if (!addressDisabled && shippingInfo.streetAddress.length && auth) {
        const addresses = await GetGooglePlacesAddress(
          shippingInfo.streetAddress,
          auth.token,
          setLoggedOut,
        );
        setAutocompleteAddressList(addresses);
      }
    };
    getAutocompleteAddresses();
  }, [shippingInfo.streetAddress, addressDisabled]);

  useEffect(() => {
    const getAutocompleteAddressesDetails = async () => {
      if (selectedAutoCompleteAddress && auth) {
        const details = await GetGooglePlacesAddressDetails(
          selectedAutoCompleteAddress,
          auth.token,
          setLoggedOut,
        );

        if (details) {
          onShippingInfoChange({
            ...shippingInfo,
            fullAddress: details.fullAddress || "",
            streetAddress: `${details.houseNumber} ${details.streetName}` || "",
            state:
              statesArray.find((state) =>
                state.split("-")[0].includes(details.state),
              ) || details.state,
            city: details.city || "",
            zipCode: details.zip || "",
          });
          setSelectedAutoCompleteAddress(undefined);
          setAutocompleteAddressList([]);
        }
      }
    };
    getAutocompleteAddressesDetails();
  }, [selectedAutoCompleteAddress]);

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onShippingInfoChange({
      ...shippingInfo,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <>
      {mode === "order" && (
        <>
          <ShipKitCheckboxWrapper
            control={
              <Checkbox
                checked={shipToPatient}
                name="isShipToPatient"
                disabled={disabled}
                onChange={(e) => onShipToPatientChange(e.target.checked)}
              />
            }
            label={
              <Box sx={{ py: 1 }}>
                <ShipKitTitle>
                  Ship blood collection kit to patient
                </ShipKitTitle>
                {GetTypographyContent({
                  content:
                    "We'll ship a Unity Complete\u00ae collection kit to the patient's home and include a pre-paid return label.",
                  size: "small",
                  textColor: "greyMedium",
                })}
              </Box>
            }
          />
        </>
      )}

      {showForm && (
        <>
          <StyledFormLabel sx={{ display: "block" }}>
            Shipping Information
          </StyledFormLabel>
          <CheckboxLabelWrapper
            control={
              <Checkbox
                checked={sameAsPatientAddress}
                name="sameAsPatientShippingAddress"
                disabled={disabled}
                onChange={(e) => onSameAsPatientToggle(e.target.checked)}
              />
            }
            label={GetTypographyContent({
              content: "Same as Patient Address",
              size: "medium",
              textColor: "greyMedium",
            })}
          />

          <Grid
            container
            spacing={3}
            sx={{ py: 2, pt: 1.5 }}
            columns={{ xs: 4, sm: 10, md: 12 }}
          >
            <Grid item md={6} sm={5} xs={4}>
              <NewRequisitionTextField
                label="First Name"
                autoComplete="nope"
                id="ship-kit-first-name"
                variant="standard"
                size="small"
                placeholder="First Name"
                fullWidth
                focused
                InputLabelProps={{ shrink: true }}
                disabled={disabled || addressDisabled}
                onKeyDown={handleKeyDown}
                value={displayInfo.firstName}
                name="firstName"
                onChange={onChange}
              />
            </Grid>
            <Grid item md={6} sm={5} xs={4}>
              <NewRequisitionTextField
                label="Last Name"
                autoComplete="nope"
                id="ship-kit-last-name"
                variant="standard"
                size="small"
                placeholder="Last Name"
                fullWidth
                focused
                InputLabelProps={{ shrink: true }}
                disabled={disabled || addressDisabled}
                onKeyDown={handleKeyDown}
                value={displayInfo.lastName}
                name="lastName"
                onChange={onChange}
              />
            </Grid>
          </Grid>

          <Grid
            container
            spacing={3}
            sx={{ py: 2 }}
            columns={{ xs: 4, sm: 10, md: 12 }}
          >
            <NewReqStreetGrid $addPadding item md={8} sm={6} xs={4}>
              <FormField
                fieldTitle="Street Address"
                autoCompletePlaceholder="Street Address"
                fieldType="autocomplete"
                maxCharacters={255}
                newRequisition
                autoCompleteDropdownStyle
                autoCompleteOptions={autocompleteAddressList.map(
                  (address) => address.fullAddress,
                )}
                fieldValue={displayInfo.streetAddress}
                onFieldChange={(val) => {
                  const selectedAddress = autocompleteAddressList.find(
                    (address) => address.fullAddress === val,
                  );
                  if (selectedAddress) {
                    onShippingInfoChange({
                      ...shippingInfo,
                      fullAddress: selectedAddress.fullAddress || "",
                      streetAddress:
                        `${selectedAddress.houseNumber} ${selectedAddress.streetName}` ||
                        "",
                    });
                    setSelectedAutoCompleteAddress(selectedAddress);
                  } else {
                    onShippingInfoChange({
                      ...shippingInfo,
                      streetAddress: val as string,
                    });
                  }
                }}
                edit={!addressDisabled && !disabled}
                autoCompleteFreeSolo
              />
            </NewReqStreetGrid>

            <Grid item md={4} sm={4} xs={4}>
              <NewRequisitionTextField
                id="ship-kit-apt-suite"
                label="Apt / Unit / Suite"
                autoComplete="nope"
                variant="standard"
                size="small"
                placeholder="Apt / Unit / Suite"
                fullWidth
                focused
                InputLabelProps={{ shrink: true }}
                disabled={disabled || addressDisabled}
                onKeyDown={handleKeyDown}
                value={displayInfo.aptUnit}
                name="aptUnit"
                onChange={onChange}
              />
            </Grid>
          </Grid>

          <Grid
            container
            spacing={3}
            sx={{ py: 2 }}
            columns={{ xs: 4, sm: 9, md: 12 }}
          >
            <Grid item md={4} sm={3} xs={4}>
              <NewRequisitionTextField
                id="ship-kit-city"
                label="City"
                autoComplete="nope"
                variant="standard"
                size="small"
                placeholder="City name"
                fullWidth
                focused
                InputLabelProps={{ shrink: true }}
                disabled={disabled || addressDisabled}
                onKeyDown={handleKeyDown}
                value={displayInfo.city}
                name="city"
                onChange={onChange}
              />
            </Grid>

            <NewReqStateGrid item md={6} sm={4} xs={4}>
              <FormField
                fieldTitle="State"
                autoCompletePlaceholder="State"
                fieldType="autocomplete"
                newRequisition
                autoCompleteDropdownStyle
                autoCompleteOptions={statesArray}
                fieldValue={displayInfo.state}
                onFieldChange={(val) => {
                  onShippingInfoChange({
                    ...shippingInfo,
                    state: val as string,
                  });
                }}
                edit={!addressDisabled && !disabled}
              />
            </NewReqStateGrid>
            <Grid item md={2} sm={2} xs={4}>
              <NewRequisitionTextField
                id="ship-kit-zip"
                label="Zip Code"
                autoComplete="nope"
                variant="standard"
                size="small"
                placeholder="Zip Code"
                fullWidth
                focused
                InputLabelProps={{ shrink: true }}
                disabled={disabled || addressDisabled}
                onKeyDown={handleKeyDown}
                value={displayInfo.zipCode}
                name="zipCode"
                onChange={onChange}
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default ShipKitToPatientForm;
